import React, { useCallback, useEffect, useRef, useState } from 'react';
import './BatchLoadingMiniGame.css';

const CANVAS_WIDTH = 620;
const CANVAS_HEIGHT = 150;
const GROUND_Y = 116;
const PLAYER_X = 52;
const PLAYER_SIZE = 26;
const OBSTACLE_WIDTH = 16;
const OBSTACLE_HEIGHT = 30;

const getLevel = (score) => Math.min(9, Math.floor(score / 5) + 1);

const getPointsToNextLevel = (score) => {
  if (getLevel(score) >= 9) return 0;
  return 5 - (score % 5);
};

const getLevelConfig = (score) => {
  const level = getLevel(score);
  return {
    level,
    speed: 4.2 + (level - 1) * 0.48,
    gapMin: Math.max(62, 150 - (level - 1) * 8),
    gapRandom: Math.max(35, 130 - (level - 1) * 9),
    obstacleHeight: 26 + ((level - 1) % 4) * 4,
    jumpPower: -10.8 - Math.min(1.4, (level - 1) * 0.12)
  };
};

const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    ctx.fill();
    return;
  }

  const safeRadius = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + safeRadius, y);
  ctx.lineTo(x + width - safeRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  ctx.lineTo(x + width, y + height - safeRadius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  ctx.lineTo(x + safeRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  ctx.lineTo(x, y + safeRadius);
  ctx.quadraticCurveTo(x, y, x + safeRadius, y);
  ctx.fill();
};

const BatchLoadingMiniGame = ({ label = '기다리는 동안 점프!' }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({
    playerY: GROUND_Y - PLAYER_SIZE,
    velocityY: 0,
    obstacleX: CANVAS_WIDTH + 80,
    speed: 4,
    obstacleHeight: OBSTACLE_HEIGHT,
    score: 0,
    best: 0,
    gameOver: false,
    lastTime: 0
  });

  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const jump = useCallback(() => {
    const game = stateRef.current;
    if (game.gameOver) {
      game.playerY = GROUND_Y - PLAYER_SIZE;
      game.velocityY = 0;
      game.obstacleX = CANVAS_WIDTH + 80;
      game.speed = getLevelConfig(0).speed;
      game.obstacleHeight = getLevelConfig(0).obstacleHeight;
      game.score = 0;
      game.gameOver = false;
      setScore(0);
      setGameOver(false);
      return;
    }

    const isOnGround = game.playerY >= GROUND_Y - PLAYER_SIZE - 1;
    if (isOnGround) {
      game.velocityY = getLevelConfig(game.score).jumpPower;
    }
  }, []);

  const draw = useCallback((ctx, game) => {
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const bg = ctx.createLinearGradient(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    bg.addColorStop(0, '#f8fbff');
    bg.addColorStop(1, '#eef6ff');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Soft clouds
    ctx.fillStyle = 'rgba(49, 130, 246, 0.08)';
    drawRoundedRect(ctx, 400, 26, 54, 14, 999);
    drawRoundedRect(ctx, 458, 42, 76, 12, 999);
    drawRoundedRect(ctx, 120, 30, 64, 12, 999);

    ctx.strokeStyle = '#d7e6ff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, GROUND_Y);
    ctx.lineTo(CANVAS_WIDTH, GROUND_Y);
    ctx.stroke();

    // Tiny moving floor marks
    ctx.fillStyle = '#c8dcff';
    for (let i = 0; i < 8; i += 1) {
      const markX = ((i * 92) - (game.score * 16 + game.obstacleX * 0.08)) % CANVAS_WIDTH;
      drawRoundedRect(ctx, markX < 0 ? markX + CANVAS_WIDTH : markX, GROUND_Y + 11, 34, 4, 999);
    }

    const playerGradient = ctx.createLinearGradient(PLAYER_X, game.playerY, PLAYER_X, game.playerY + PLAYER_SIZE);
    playerGradient.addColorStop(0, '#4f9cff');
    playerGradient.addColorStop(1, '#2563eb');
    ctx.fillStyle = playerGradient;
    drawRoundedRect(ctx, PLAYER_X, game.playerY, PLAYER_SIZE, PLAYER_SIZE, 7);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(PLAYER_X + 17, game.playerY + 9, 2.2, 0, Math.PI * 2);
    ctx.fill();

    const obstacleHeight = game.obstacleHeight || OBSTACLE_HEIGHT;
    const obstacleGradient = ctx.createLinearGradient(game.obstacleX, GROUND_Y - obstacleHeight, game.obstacleX, GROUND_Y);
    obstacleGradient.addColorStop(0, '#64748b');
    obstacleGradient.addColorStop(1, '#334155');
    ctx.fillStyle = obstacleGradient;
    drawRoundedRect(ctx, game.obstacleX, GROUND_Y - obstacleHeight, OBSTACLE_WIDTH, obstacleHeight, 4);

    ctx.fillStyle = '#8b95a1';
    ctx.font = '600 12px sans-serif';
    ctx.fillText(`Lv.${getLevel(game.score)}  클릭 또는 Space로 점프`, 18, 24);

    if (game.gameOver) {
      ctx.fillStyle = 'rgba(248, 251, 255, 0.88)';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.fillStyle = '#1f2937';
      ctx.font = '800 17px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('아깝다! 다시 클릭해서 재도전', CANVAS_WIDTH / 2, 68);
      ctx.fillStyle = '#3182f6';
      ctx.font = '700 12px sans-serif';
      ctx.fillText(`이번 점수 ${game.score}점`, CANVAS_WIDTH / 2, 90);
      ctx.textAlign = 'start';
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return undefined;

    const tick = (time) => {
      const game = stateRef.current;
      const delta = Math.min((time - (game.lastTime || time)) / 16.67, 2);
      game.lastTime = time;

      if (!game.gameOver) {
        game.velocityY += 0.62 * delta;
        game.playerY = Math.min(GROUND_Y - PLAYER_SIZE, game.playerY + game.velocityY * delta);
        if (game.playerY >= GROUND_Y - PLAYER_SIZE) {
          game.velocityY = 0;
        }

        game.obstacleX -= game.speed * delta;
        if (game.obstacleX < -OBSTACLE_WIDTH) {
          game.score += 1;
        const nextConfig = getLevelConfig(game.score);
        game.obstacleX = CANVAS_WIDTH + nextConfig.gapMin + Math.random() * nextConfig.gapRandom;
        game.speed = nextConfig.speed;
        game.obstacleHeight = nextConfig.obstacleHeight;
          game.best = Math.max(game.best, game.score);
          setScore(game.score);
          setBest(game.best);
        }

        const playerRight = PLAYER_X + PLAYER_SIZE;
        const playerBottom = game.playerY + PLAYER_SIZE;
        const obstacleRight = game.obstacleX + OBSTACLE_WIDTH;
        const obstacleTop = GROUND_Y - (game.obstacleHeight || OBSTACLE_HEIGHT);
        const hit =
          playerRight > game.obstacleX &&
          PLAYER_X < obstacleRight &&
          playerBottom > obstacleTop &&
          game.playerY < GROUND_Y;

        if (hit) {
          game.gameOver = true;
          setGameOver(true);
        }
      }

      draw(ctx, game);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [draw]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        jump();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [jump]);

  return (
    <div className="batch-loading-game">
      <div className="batch-loading-game__header">
        <span>{label}</span>
        <span>Lv.{getLevel(score)} · 점수 {score} · 최고 {best}</span>
      </div>
      <div className="batch-loading-game__meta">
        <span>{getPointsToNextLevel(score) > 0 ? `다음 레벨까지 ${getPointsToNextLevel(score)}점` : '최고 레벨 도전 중'}</span>
        {score >= 10 && <span>속도 상승 중</span>}
      </div>
      <button
        type="button"
        className="batch-loading-game__stage"
        onClick={jump}
        aria-label={gameOver ? '미니 게임 다시 시작' : '미니 게임 점프'}
      >
        <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} />
      </button>
    </div>
  );
};

export default BatchLoadingMiniGame;
